import { MapPinIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import type { Company } from '../types/company.types';

/* SUBSCRIPTIONS_DISABLED
interface SubscriptionInfo {
  planName: string | null;
  status: string;
  isActive: boolean;
}
*/

interface CompanyCardProps {
  company: Company;
  isSelected?: boolean;
  isSelecting?: boolean;
  isDefault?: boolean;
  // SUBSCRIPTIONS_DISABLED: subscriptionInfo?: SubscriptionInfo;
  onSelect: (companyId: string) => void;
}

/**
 * Degradados del avatar (Claude Design · SeleccionaEmpresa.dc.html). El diseño
 * alterna varios; acá la variante se elige con un hash del id de la compañía,
 * asi que cada empresa siempre se ve igual entre recargas y entre páginas.
 * Nada aleatorio.
 */
const AVATAR_GRADIENTS = [
  'from-teal-500 to-[#6b239e]',
  'from-[#6b239e] to-teal-500',
  'from-teal-500 to-[#3a7ac7]',
  'from-[#6b239e] to-ink-850',
  'from-accent-500 to-[#6b239e]',
];

function gradientFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

/**
 * Iniciales del nombre — hasta dos letras.
 *
 * `name` puede venir `null` de Aludra (confirmado en producción: 3 de 12
 * compañías de un tenant real con `"name": null` — no es un caso raro de
 * borde, pasa con cierta frecuencia). El tipo `Company.name` lo declara
 * como `string` no-nulable, pero eso es lo que DEBERÍA venir, no lo que
 * realmente llega — antes esto tiraba `TypeError: Cannot read properties
 * of null (reading 'trim')` y tumbaba la pantalla COMPLETA de selección de
 * compañías (todas las tarjetas, no solo la de la compañía sin nombre).
 */
function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Nombre a mostrar cuando Aludra no manda `name` — cae a lo que sí tenga. */
function displayNameOf(company: Company): string {
  return company.name || company.fullName || company.code || 'Sin nombre';
}

export function CompanyCard({ company, isSelected, isSelecting, isDefault, onSelect }: CompanyCardProps) {
  return (
    <button
      onClick={() => onSelect(company.id)}
      disabled={isSelecting}
      data-testid={`company-card-${company.id}`}
      className={`group relative flex w-full flex-col overflow-hidden rounded-2xl border p-[22px] text-left shadow-[0_1px_2px_rgba(10,34,51,.03),0_10px_24px_-14px_rgba(10,34,51,.12)] transition-[transform,border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
        isSelected
          ? 'border-teal-500/40 bg-teal-500/[.04]'
          : 'border-[#ececec] bg-white hover:-translate-y-0.5 hover:border-[#dcdcdf] hover:shadow-[0_1px_2px_rgba(10,34,51,.04),0_20px_44px_-20px_rgba(10,34,51,.24)] dark:border-secondary-700 dark:bg-secondary-800'
      } ${isSelecting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      {/* Banda de marca que aparece al pasar el mouse */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-teal-500 to-[#6b239e] opacity-0 transition-opacity group-hover:opacity-100"
      />

      {isSelected && (
        <span className="absolute right-4 top-4" data-testid={`company-card-selected-badge-${company.id}`}>
          <CheckCircleIcon className="h-5 w-5 text-[#0d9488]" />
        </span>
      )}

      {/* Cabecera: avatar + nombre + RUC + código */}
      <div className="flex items-start gap-3.5">
        {company.image ? (
          <img
            src={company.image}
            alt={displayNameOf(company)}
            className="h-12 w-12 flex-shrink-0 rounded-xl object-contain bg-white"
          />
        ) : (
          <span
            className={`inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-[15px] font-extrabold tracking-[-0.02em] text-white shadow-[0_4px_12px_-4px_rgba(107,35,158,.35)] ${gradientFor(company.id)}`}
          >
            {initialsOf(company.name)}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div
              className="truncate text-[15.5px] font-extrabold leading-tight tracking-[-0.015em] text-ink-850 dark:text-white"
              data-testid={`company-card-name-${company.id}`}
            >
              {displayNameOf(company)}
            </div>
            {isDefault && (
              <span
                className="inline-flex flex-shrink-0 items-center rounded-full bg-teal-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0d9488]"
                data-testid={`company-card-default-badge-${company.id}`}
              >
                Predeterminada
              </span>
            )}
          </div>
          {company.ruc && (
            <div
              className="mt-1.5 font-mono text-[12px] font-semibold tracking-[0.02em] text-secondary-500"
              data-testid={`company-card-ruc-${company.id}`}
            >
              RUC {company.ruc}
            </div>
          )}
          {company.code && (
            <div
              className="mt-0.5 font-mono text-[11px] font-medium tracking-[0.02em] text-secondary-400"
              data-testid={`company-card-code-${company.id}`}
            >
              Código {company.code}
            </div>
          )}
        </div>
      </div>

      {/* Datos de contacto */}
      {(company.address || company.email) && (
        <div className="mt-4 flex flex-1 flex-col gap-2 border-t border-dashed border-[#eef0f2] pt-4 dark:border-secondary-700">
          {company.address && (
            <div className="flex min-w-0 items-center gap-2 text-[13px] text-secondary-500">
              <MapPinIcon className="h-3 w-3 flex-shrink-0 text-secondary-400" />
              <span className="min-w-0 truncate" title={company.address}>{company.address}</span>
            </div>
          )}
          {company.email && (
            <div className="flex min-w-0 items-center gap-2 text-[13px] text-secondary-500">
              <EnvelopeIcon className="h-3 w-3 flex-shrink-0 text-secondary-400" />
              <span className="min-w-0 truncate" title={company.email}>{company.email}</span>
            </div>
          )}
        </div>
      )}

      {/* Estado seleccionado */}
      {isSelected && (
        <div className="mt-4 flex items-center gap-2 border-t border-dashed border-[#eef0f2] pt-3.5 text-[14px] font-bold text-[#0d9488] dark:border-secondary-700">
          <span data-testid={`company-card-selected-label-${company.id}`}>Empresa Seleccionada</span>
        </div>
      )}
    </button>
  );
}
